import { Box, Step, StepLabel, Stepper } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useCreateContractMutation, useUpdateContractMutation, useCreateContractExtractionMutation, useLazyGetContractExtractionQuery, useCreateRebatesMutation } from '../../api/contractSlice';
import { calculateThresholdMax, getListOfPeriods } from '../../utils/rebatePeriods';
import { UserContext } from '../Hooks/useUser';
import EditContract from './EditContract';
import EditRebates from './EditRebates';
import ReviewContract from './ReviewContract';
import UploadContract from './UploadContract';

const STEPS = ['Upload File', 'Edit Contract Information', 'Edit Rebates', 'Review'];

function CreateContract() {
    const MANUAL_PERIOD_TYPES = ['custom', 'unknown'];
    const { user } = useContext(UserContext);
    const [createContract] = useCreateContractMutation();
    const [updateContract] = useUpdateContractMutation();
    const [createContractExtraction] = useCreateContractExtractionMutation();
    const [triggerGetExtraction] = useLazyGetContractExtractionQuery();
    const [createRebates] = useCreateRebatesMutation();

    const pollExtraction = async (extractionId) => {
        const MAX_POLLS = 20;

        for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise((r) => setTimeout(r, 5000));

            const parsedOrNull = await triggerGetExtraction(extractionId)
                .unwrap()
                .then((extraction) => {
                    if (extraction.status === 'COMPLETED') {
                        const parsed = typeof extraction.result === 'string'
                            ? JSON.parse(extraction.result)
                            : extraction.result;
                        console.log('[Extraction] Completed:', parsed);
                        return parsed;
                    }

                    if (extraction.status === 'FAILURE') {
                        console.error('[Extraction] Failed:', extraction);
                        return null;
                    }

                    // still processing
                    return undefined;
                })
                .catch((e) => {
                    console.error('[Extraction] Polling error:', e);
                    return null;
                });

            if (parsedOrNull === null) return null;  // failure/error
            if (parsedOrNull !== undefined) return parsedOrNull; // completed
        }
        
        console.warn('[Extraction] Polling timed out for extraction:', extractionId);
        return null;
    };

    const [activeStep, setActiveStep] = useState(0);
    const [fileName, setFileName] = useState('');
    const [parsedData, setParsedData] = useState(null);
    const [contract, setContract] = useState(null);
    const [rebatesData, setRebatesData] = useState(null);

    const handleParsing = async (data) => {
        const name = data._fileName ?? '';
        const file = data._file ?? null;
        setFileName(name);
        try {
            const created = await createContract({ name, organizationId: user.organization.id }).unwrap();
            setContract(created);

            if (file) {
                const extraction = await createContractExtraction({ contractId: created.id, file }).unwrap();
                const result = await pollExtraction(extraction.id);
                if (result) {
                    setParsedData(result);
                }
            }

            setActiveStep(1);

        } catch {
            // error notification handled in slice
        }
    };

    const handleSkip = () => {
        setParsedData(null);
        setActiveStep(1);
    };

    const handleSave = async ({name, external_id, start_date, effective_start_date, end_date}) => {
        if (!contract?.id) {
            // no file uploaded — create contract now with the manually entered name
            await createContract({ name, organizationId: user.organization.id })
                .unwrap()
                .then((created) => {
                    setContract(created);
                    setActiveStep(2);
                })
                .catch(() => {
                    // error notification handled in slice
                });
        } else {
            // contract already created — patch only if the user changed something
            const norm = (v) => v ?? '';
            const hasChanges = norm(name) !== norm(contract.name) ||
                norm(external_id) !== norm(contract.external_id) ||
                norm(start_date) !== norm(contract.start_date) ||
                norm(effective_start_date) !== norm(contract.effective_start_date) ||
                norm(end_date) !== norm(contract.end_date);

            if (!hasChanges) {
                setActiveStep(2);
                return;
            }

            await updateContract({ contractId: contract.id, data: { name, external_id, start_date, effective_start_date, end_date } })
                .unwrap()
                .then((updated) => {
                    setContract((prev) => ({ ...prev, name, external_id, start_date, effective_start_date, end_date, ...updated }));
                    setActiveStep(2);
                })
                .catch(() => {
                    // error notification handled in slice
                });
        }
    };

    const handleBackToContract = () => {
        setActiveStep(1);
    };

    const handleSkipRebates = () => {
        setRebatesData(null);
        setActiveStep(3);
    };

    const handleSaveRebates = async (rebates) => {
        if (!contract?.id) return;

        const normalizePeriods = (periods) => {
            if (!Array.isArray(periods)) {
                return [];
            }

            return periods.flatMap((period) => (Array.isArray(period) ? period : [period])).filter(Boolean);
        };

        const rebatesData = rebates.map((rebate) => {
            const normalizedPeriods = normalizePeriods(rebate.periods);
            const rebateStartDate = rebate.startDate ?? normalizedPeriods[0]?.startDate ?? '';
            const rebateEndDate = rebate.endDate ?? normalizedPeriods[normalizedPeriods.length - 1]?.endDate ?? '';
            const shouldSlice = !MANUAL_PERIOD_TYPES.includes(rebate.periodType);

            const periodsToPersist = shouldSlice
                ? getListOfPeriods(rebateStartDate, rebateEndDate, rebate.periodType).map((slice, index) => {
                    const source = normalizedPeriods[index] ?? normalizedPeriods[0] ?? {};

                    return {
                        ...source,
                        startDate: slice.startDate,
                        endDate: slice.endDate,
                    };
                })
                : normalizedPeriods;

            return {
                rebate_type: rebate.programType,
                period_type: rebate.periodType,
                periods: periodsToPersist.map((period) => ({
                    start_date: period.startDate ?? rebateStartDate,
                    end_date: period.endDate ?? rebateEndDate,
                    rules: {
                        rule_description: period.formulaDescription ?? period.name ?? '',
                        tiers: (() => {
                            const sourceTiers = Array.isArray(period.tiers) && period.tiers.length > 0
                                ? period.tiers
                                : [{ threshold: period.threshold ?? null, rebateValue: period.rebateValue ?? null }];

                            return sourceTiers.map((tier, index) => {
                                const thresholdMin = tier.threshold;
                                const nextThresholdMin = sourceTiers[index + 1]?.threshold;
                                const calculatedMax = calculateThresholdMax(thresholdMin, nextThresholdMin);

                                return {
                                    threshold_min: thresholdMin,
                                    threshold_max: calculatedMax === '' ? null : calculatedMax,
                                    flat_rebate_value: rebate.rebateValueType === 0 ? tier.rebateValue : null,
                                    rebate_percentage: rebate.rebateValueType === 1 ? tier.rebateValue : null,
                                    per_unit_rebate_value: rebate.rebateValueType === 2 ? tier.rebateValue : null,
                                };
                            });
                        })(),
                    },
                })),
            };
        });

        await createRebates({
            contractId: contract.id,
            rebates: rebatesData,
        }).unwrap();

        setRebatesData(rebates);
        setActiveStep(3);
    };

    const handleBack = () => {
        setActiveStep(0);
        setFileName('');
        setParsedData(null);
        setContract(null);
        setRebatesData(null);
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, pt: 4 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && (
                <UploadContract onParsed={handleParsing} onSkip={handleSkip} />
            )}

            {activeStep === 1 && (
                <EditContract
                    contractData={contract}
                    parsedData={parsedData}
                    fileName={fileName}
                    onBack={handleBack}
                    onSave={handleSave}
                />
            )}

            {activeStep === 2 && (
                <EditRebates
                    contractData={contract}
                    parsedData={parsedData}
                    rebatesData={rebatesData}
                    onBack={handleBackToContract}
                    onSave={handleSaveRebates}
                    onSkip={handleSkipRebates}
                />
            )}

            {activeStep === 3 && (
                <ReviewContract
                    contractData={contract}
                    rebatesData={rebatesData}
                />
            )}
        </Box>
    );
}

export default CreateContract;
